import {
  infiniteQueryOptions,
  keepPreviousData,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";

import { sessionApi } from "@shared/api";
import { useScrollFetchRef } from "@shared/libs/api-client/hooks";

interface UseUserSessionListParams {
  pageSize?: number;
}

/* eslint-disable perfectionist/sort-objects */
export const sessionsListQueryOptions = ({
  pageSize = 20,
}: UseUserSessionListParams) =>
  infiniteQueryOptions({
    queryKey: ["sessions-list", pageSize],
    queryFn: ({ pageParam }) =>
      sessionApi.getApiV1SessionsList({ page: pageParam, pageSize }),
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage.data;
      if (pagination.page >= pagination.pageCount - 1) {
        return undefined;
      }
      return pagination.page + 1;
    },
    select: (data) => {
      const flatList = data.pages.flatMap((page) => page.data.data);

      const lastPage = data.pages[data.pages.length - 1];

      return {
        list: flatList,
        pagination: lastPage.data.pagination,
        sort: lastPage.data.sort,
      };
    },
  });

export const useSessionsList = (params: UseUserSessionListParams = {}) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
    isPending,
  } = useSuspenseInfiniteQuery(sessionsListQueryOptions(params));
  const loadMoreRef = useScrollFetchRef({
    fetch: fetchNextPage,
    hasNext: hasNextPage,
    isFetching: isFetchingNextPage,
  });

  return {
    error,
    loadMoreRef,
    pagination: data.pagination,
    refetch,
    sessions: data.list,
    sort: data.sort,
    isPending: isPending || isFetchingNextPage,
  };
};
